import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useEffect } from 'react';
import {
  Controller,
  FieldValues,
  SubmitHandler,
  UseFormSetError,
  useForm,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router';

import { SharingInvitationStatus } from '@/shared/api/protocol-ts/model/dto_sharing_pb';
import clearCat from '@/shared/assets/images/cats/clear.png';
import { PATHS } from '@/shared/config';
import { trackEvent } from '@/shared/lib';
import { Button, Input, Layout, Modal, StatusCatImage } from '@/shared/ui';

import { PATH_TO_PATIENTS_SHARED_WITH_ME_LIST } from '@/entities/patient';

import { addSharedPatientModalSchema } from '@/features/sharePatient/config/schema';
import { AddSharedPatientModalPayloadType } from '@/features/sharePatient/config/types';

import { useOrganizationInvite } from './hooks';
import styles from './OrganizationInvite.module.scss';

export const OrganizationInvite: FC = () => {
  const { formatMessage, formatDate } = useIntl();

  const { token } = useParams();

  const navigate = useNavigate();

  const {
    invitationStatus,
    invitationCreatedAt,
    senderFullName,
    acceptSharingPatientInviteHandle,
    navigateForAcceptedInvite,
    navigateToSharedWithMeList,
    showCancelledInvitationToast,
  } = useOrganizationInvite(token as string);

  const isInvitationStatusAccepted =
    invitationStatus ===
    SharingInvitationStatus.SharingInvitationStatus_Accepted;

  const isInvitationStatusCancelled =
    invitationStatus ===
    SharingInvitationStatus.SharingInvitationStatus_Cancelled;

  const isInvitationStatusPending =
    invitationStatus ===
    SharingInvitationStatus.SharingInvitationStatus_Pending;

  // Check in case the user follows the link with the token instead of entering it manually
  useEffect(() => {
    if (isInvitationStatusAccepted) {
      navigateForAcceptedInvite();
    }

    if (isInvitationStatusCancelled) {
      showCancelledInvitationToast();

      navigateToSharedWithMeList();
    }
  }, [invitationStatus]);

  useEffect(() => {
    trackEvent({
      eventName: 'OrganizationInvite Page Viewed',
      isPageViewEvent: true,
    });
  }, []);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    setError,
  } = useForm<AddSharedPatientModalPayloadType>({
    resolver: yupResolver(addSharedPatientModalSchema),
    mode: 'onChange',
  });

  const isInviteCanceled =
    errors.accessCode && errors.accessCode.type === 'canceledCode';

  const onSubmit: SubmitHandler<AddSharedPatientModalPayloadType> = async (
    data,
  ) => {
    const { accessCode } = data;

    return isInviteCanceled
      ? navigate(PATHS.main)
      : acceptSharingPatientInviteHandle(
          accessCode,
          setError as UseFormSetError<FieldValues>,
        );
  };

  return (
    <Layout>
      <Layout.Content>
        <Layout.Main>
          <Modal
            data-component="organization-invite-form"
            isOpen
            className={styles.modal}
            headerClassName={styles.modalHeader}
            bodyClassName={styles.modalBody}
            containerClassName={styles.modalContainer}
            shouldRenderCloseIconButton={false}
            hideFooter
          >
            <StatusCatImage
              statusImageSrc={clearCat}
              altText={'statusMessage'}
              isLoading={true}
            />

            {isInvitationStatusPending && (
              <>
                <div className={cn(styles.notification, 'p1')}>
                  <FormattedMessage
                    id="addSharingPatient.notification"
                    defaultMessage="Patient’s page is protected."
                  />

                  <br />

                  <FormattedMessage
                    id="addSharingPatient.contact"
                    defaultMessage="Please, contact {name} to get the access code."
                    values={{
                      name: senderFullName,
                    }}
                  />
                </div>

                {!!invitationCreatedAt && (
                  <div className="p2">
                    <FormattedMessage
                      id="addSharingPatient.sharingDate"
                      defaultMessage="Sharing date:"
                    />{' '}
                    {formatDate(invitationCreatedAt.toDate(), {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  </div>
                )}

                <form
                  id="addSharedPatientForm"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Controller
                    control={control}
                    name="accessCode"
                    render={({
                      field: { ref, value, name, onBlur, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className={styles.input}
                        required
                        ref={ref}
                        value={value}
                        name={name}
                        type="accessCode"
                        onBlur={onBlur}
                        onChange={onChange}
                        error={error?.message}
                        placeholder={formatMessage({
                          id: 'addSharingPatient.placeholder',
                          defaultMessage: 'Enter access code here',
                        })}
                        alignText="center"
                      />
                    )}
                  />
                </form>

                <Button
                  variant="primary"
                  size="large"
                  type="submit"
                  form="addSharedPatientForm"
                  loading={isSubmitting}
                >
                  {isInviteCanceled ? (
                    <FormattedMessage
                      id="addSharingPatient.goToMainPage"
                      defaultMessage="Go to the main page"
                    />
                  ) : (
                    <FormattedMessage
                      id="global.submit"
                      defaultMessage="Submit"
                    />
                  )}
                </Button>

                <Link
                  to={PATH_TO_PATIENTS_SHARED_WITH_ME_LIST}
                  className={cn(styles.addLater, 'p2')}
                >
                  <FormattedMessage
                    id="addSharingPatient.addLater"
                    defaultMessage="I’ll do it later"
                  />
                </Link>
              </>
            )}
          </Modal>
        </Layout.Main>
      </Layout.Content>
    </Layout>
  );
};
